import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';

import EventJoinForm from '../Forms/event-join-form-component';
import useDisableScrolling from '../../Hooks/useDisableScrolling';
import { getEventsScreenData } from '../../Public/events-screen-data';

/**
 * "Join event" modal, iOS-style bottom sheet on mobile, centered on desktop.
 * Wraps the quick lead form. Portaled to body so `fixed` is never trapped.
 */
export default function EventJoinModal({ event, lang = 'he', dir = 'rtl', onClose }) {
    const { join: j } = getEventsScreenData(lang);
    useDisableScrolling(!!event);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!event || typeof document === 'undefined') return null;

    return ReactDOM.createPortal(
        <motion.div
            className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            dir={dir}
        >
            <motion.div
                onClick={(e) => e.stopPropagation()}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                className="relative w-full sm:w-[420px] max-h-[92vh] overflow-y-auto bg-primary-900 rounded-t-[28px] sm:rounded-[28px] border border-secondary-500/30 shadow-2xl shadow-black/60"
            >
                {/* Drag handle (mobile) */}
                <div className="sm:hidden sticky top-0 pt-3 pb-1 flex justify-center bg-primary-900/95 backdrop-blur-sm z-10">
                    <span className="h-1.5 w-11 rounded-full bg-white/25" />
                </div>

                <button
                    onClick={onClose}
                    aria-label="close"
                    className="absolute top-3 end-3 z-20 w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                    ✕
                </button>

                <div className="p-5 sm:p-6 pt-4">
                    <h3 className="font-secondary text-xl font-extralight text-white">{j.title}</h3>
                    <p className="mt-0.5 mb-4 text-sm font-bold text-secondary-300 font-primary line-clamp-1">{event.title}</p>
                    <EventJoinForm event={event} lang={lang} dir={dir} />
                </div>
            </motion.div>
        </motion.div>,
        document.body
    );
}
